import { FC, MutableRefObject, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createContext } from 'use-context-selector';

import useInspections, { UseInspectionsHook } from '../hooks/useInspections';
import useSearchAmenities, { UseSearchAmenitiesHook } from '../hooks/useSearchAmenities';
import { TInspection } from '../services/Amenities/types';
import {
  useFetchInspectionAmenities,
  useSyncInspection,
} from '../services/Amenities/useAmenitiesService';

interface IAmenitiesContext {
  inspectionAmenities: TInspection | undefined;
  loadingInspectionAmenities: boolean;
  loadingSyncInspection: boolean;
  errorSyncInspection: any;
  successSyncInspection: boolean | undefined;
  errorInspectionAmenities: any;
  selectedProperty: string;
  selectedPropertyRef: MutableRefObject<string>;
  handleChangeProperty: (value: string) => void;
  handleSearch: () => void;
  handleSyncInspection: () => void;
  searchAmenities: UseSearchAmenitiesHook;
  inspections: UseInspectionsHook;
}

export const AmenitiesContext = createContext<IAmenitiesContext>({
  inspectionAmenities: undefined,
  loadingInspectionAmenities: false,
  loadingSyncInspection: false,
  successSyncInspection: undefined,
  errorSyncInspection: null,
  errorInspectionAmenities: null,
  selectedProperty: '',
  selectedPropertyRef: { current: '' },
  handleChangeProperty: () => {},
  handleSearch: () => {},
  handleSyncInspection: () => {},
  searchAmenities: {} as UseSearchAmenitiesHook,
  inspections: {} as UseInspectionsHook,
});

export const AmenitiesProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: inspectionAmenities,
    loading: loadingInspectionAmenities,
    error: errorInspectionAmenities,
    fetchInspectionAmenities,
  } = useFetchInspectionAmenities();

  const searchAmenities = useSearchAmenities({ inspectionAmenities });
  const {
    success: successSyncInspection,
    loading: loadingSyncInspection,
    error: errorSyncInspection,
    syncInspection,
  } = useSyncInspection();

  const inspections = useInspections();

  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const selectedPropertyRef = useRef<string>(selectedProperty);
  const hasSyncedRef = useRef(false);

  const handleSyncInspection = async () => {
    if (selectedProperty) {
      selectedPropertyRef.current = selectedProperty;
      await syncInspection(selectedProperty);
    }
  };

  const handleChangeProperty = (value: string) => {
    setSelectedProperty(value);
  };

  const handleSearch = async () => {
    if (!selectedProperty) return;

    selectedPropertyRef.current = selectedProperty;
    const response = await fetchInspectionAmenities(selectedProperty);

    if (!response && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      await syncInspection(selectedProperty);
    } else {
      hasSyncedRef.current = false;
    }
  };

  useEffect(() => {
    selectedPropertyRef.current = selectedProperty;
    handleSearch();
  }, [selectedProperty]);

  useEffect(() => {
    if (!loadingSyncInspection && successSyncInspection) {
      handleSearch();
    }
  }, [successSyncInspection, loadingSyncInspection]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('updated', inspectionAmenities?.updated_at || '');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true },
    );
  }, [inspectionAmenities]);

  const value = useMemo(
    () => ({
      inspectionAmenities,
      loadingInspectionAmenities,
      errorInspectionAmenities,
      loadingSyncInspection,
      errorSyncInspection,
      successSyncInspection,
      selectedProperty,
      selectedPropertyRef,
      handleChangeProperty,
      handleSearch,
      handleSyncInspection,
      searchAmenities,
      inspections,
    }),
    [
      inspectionAmenities,
      loadingInspectionAmenities,
      errorInspectionAmenities,
      loadingSyncInspection,
      errorSyncInspection,
      successSyncInspection,
      selectedProperty,
      selectedPropertyRef,
      handleChangeProperty,
      handleSearch,
      handleSyncInspection,
      searchAmenities,
      inspections,
    ],
  );

  return <AmenitiesContext.Provider value={value}>{children}</AmenitiesContext.Provider>;
};
